import React from 'react';
import { styleDefinitions as styles } from '../../theme/styles';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { animations } from '../../theme/animations';

interface SortableRowProps {
  id: string;
  row: any;
  children: React.ReactNode;
  level: number;
  draggedType?: string;
  className?: string;
}

export const SortableRow = React.memo<SortableRowProps>(({
  id,
  row,
  children,
  level,
  draggedType,
  className = ''
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  // Determine if this row is draggable
  const itemType = id.startsWith('level1-') ? 'category' : 
                  id.startsWith('level2::') ? 'subcategory' : 'item';
  const isDraggable = itemType === 'item' || itemType === 'category';
  // In table view, level is 0 for all rows
  const isTableView = level === 0;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver: isSortableOver} = useSortable({ 
    id,
    disabled: !isDraggable
  });

  const style = {
    transform: isDraggable ? CSS.Transform.toString(transform) : undefined,
    transition: isDraggable ? transition : undefined,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const};

  // Determine if we should show the drop indicator
  const showDropIndicator = isSortableOver && !isDragging;
  
  // Show indicator AFTER categories/subcategories to show items will go inside
  // Or show indicator between categories when reordering them
  const shouldShowIndicatorAfter = showDropIndicator && (
    (draggedType === 'item' && (itemType === 'category' || itemType === 'subcategory')) ||
    (draggedType === 'category' && itemType === 'category')
  );
  
  // Show indicator BEFORE items when dragging over other items
  const shouldShowIndicatorBefore = showDropIndicator && draggedType === 'item' && 
    itemType === 'item';

  // For table view, render with drag handle in first cell
  if (isTableView) {
    return (
      <tr
        ref={setNodeRef}
        style={{
          ...style,
          ...styles.components.modernTable.row,
          ...(isHovered ? styles.components.modernTable.rowHover : {}),
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-sortable-id={id}
      >
        <td style={{...styles.components.modernTable.cell, ...styles.components.modernTable.dragHandleCell}}>
          {isDraggable ? (
            <div
              {...attributes}
              {...listeners}
              style={{
                ...styles.components.modernTable.actionButton,
                ...(isHovered ? {
                  ...styles.components.modernTable.actionButtonHover,
                  ...animations.dragHandleGlow
                } : {}),
                ...(isTableView ?
                  (isHovered ? styles.components.modernTable.dragHandleVisible : styles.components.modernTable.dragHandleHidden) :
                  styles.components.modernTable.dragHandleVisible
                ),
              }}
            >
              <GripVertical size={16} style={{
                ...styles.components.modernTable.dragHandleIcon,
                ...(isHovered ? styles.components.modernTable.dragHandleIconHover : {})
              }} />
            </div>
          ) : (
            <div style={styles.components.modernTable.dragHandlePlaceholder} />
          )}
        </td>
        {children}
      </tr>
    );
  }

  // For tree view, render with drag handle and indicators
  return (
    <>
      {shouldShowIndicatorBefore && (
        <tr style={styles.utility.pointerEventsNone}>
          <td colSpan={100} style={styles.spacing.paddingNone}>
            <div style={styles.utility.border} />
          </td>
        </tr>
      )}
      <tr
        ref={setNodeRef}
        style={style}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <td style={styles.spacing.paddingNone}>
          {isDraggable ? (
            <div
              {...attributes}
              {...listeners}
              style={{
                ...styles.table.dragHandle,
                ...(isHovered ? {
                  ...styles.table.dragHandleVisible,
                  ...animations.dragHandleGlow
                } : {})
              }}
            >
              <GripVertical size={12} style={{
                ...styles.typography.textTertiary,
                ...(isHovered ? styles.components.modernTable.dragHandleIconHover : {})
              }} />
            </div>
          ) : (
            <div style={styles.extended.p05} />
          )}
        </td>
        {children}
      </tr>
      {shouldShowIndicatorAfter && (
        <tr style={styles.utility.pointerEventsNone}>
          <td colSpan={100} style={styles.spacing.paddingNone}>
            <div style={styles.colors.primary} />
          </td>
        </tr>
      )}
    </>
  );
});